/**
 * /programs/:id/preview — welcome card only. CTA inside the hero advances
 * the trainer to /preview/days.
 */

import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useProgram } from '../../services/programs';
import { useImageUrl } from '../../services/uploads';
import PreviewWelcomeHero from '../../components/programs/preview/PreviewWelcomeHero';
import PreviewPageShell from '../../components/programs/preview/PreviewPageShell';

export default function ProgramPreviewWelcomePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: program, isLoading, isError } = useProgram(id);
  const [coverUrl] = useImageUrl(program?.header_image ?? null);

  if (isLoading) {
    return (
      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 py-8">
        <div className="aspect-[21/10] rounded-[28px] bg-text-tertiary/10 animate-pulse" />
      </div>
    );
  }
  if (isError || !program || !id) {
    return (
      <div className="max-w-[640px] mx-auto px-4 py-12 text-center">
        <p className="text-sm text-error-500 mb-4">Could not load the program preview.</p>
        <Link to="/programs" className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to Programs
        </Link>
      </div>
    );
  }

  return (
    <PreviewPageShell
      programId={id}
      upHref={`/programs/${id}`}
      upLabel="Back to Program"
      programIsActive={program.status === 'active'}
    >
      <PreviewWelcomeHero
        coverUrl={coverUrl ?? undefined}
        title={program.name || 'Welcome to the company'}
        description={program.description ?? undefined}
        durationLabel={`${program.duration_days} ${program.duration_days === 1 ? 'day' : 'days'}`}
        cta={{
          label: 'Start the program',
          onClick: () => navigate(`/programs/${id}/preview/days`),
        }}
      />
    </PreviewPageShell>
  );
}
